import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Box,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Alert,
  Chip,
  CircularProgress,
  Select,
  Option,
} from "@mui/joy";
import { PaperPlaneTilt, WarningCircle } from "@phosphor-icons/react";
import {
  PhotoReportControls,
  type PhotoReportStatus,
} from "./PhotoReportControls";
import type { Photo } from "../../types";
import { apiClient } from "../../lib/api";
import type { PhotoEmailRecipients } from "../../lib/api";

interface EmailPhotosModalProps {
  open: boolean;
  onClose: () => void;
  photos: Photo[];
  opportunityId?: number;
  jobId?: number;
  onSent?: () => void;
}

/** Turn a comma/semicolon/space separated field into a list of addresses. */
const parseAddresses = (value: string): string[] =>
  value
    .split(/[,;\s]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const looksLikeEmail = (value: string) =>
  /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);

export const EmailPhotosModal: React.FC<EmailPhotosModalProps> = ({
  open,
  onClose,
  photos,
  opportunityId,
  jobId,
  onSent,
}) => {
  const [format, setFormat] = useState<"attachments" | "pdf">("attachments");
  const [report, setReport] = useState<PhotoReportStatus | null>(null);
  const onReportReady = useCallback(
    (value: PhotoReportStatus | null) => setReport(value),
    [],
  );
  const sendingRef = useRef(false);
  const toEdited = useRef(false);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [recipients, setRecipients] = useState<PhotoEmailRecipients | null>(
    null,
  );
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill from the record's contact so the common case is one click. Fetched
  // on open rather than up front — most gallery visits never email anything.
  useEffect(() => {
    if (!open) return;

    let active = true;
    toEdited.current = false;
    setTo("");
    setRecipients(null);
    setError(null);
    setLoadingRecipients(true);
    apiClient
      .getPhotoEmailRecipients({ opportunity: opportunityId, job: jobId })
      .then((response) => {
        if (!active) return;
        const data = response.data;
        setRecipients(data);
        if (!toEdited.current)
          setTo(data.to.map((entry) => entry.email).join(", "));
      })
      .catch(() => {
        // Not fatal — the sender can still type an address.
        if (active) setRecipients(null);
      })
      .finally(() => {
        if (active) setLoadingRecipients(false);
      });
    return () => {
      active = false;
    };
  }, [open, opportunityId, jobId]);

  useEffect(() => {
    if (!open) {
      setFormat("attachments");
      setReport(null);
      setCc("");
      setSubject("");
      setMessage("");
      setError(null);
    }
  }, [open]);

  const toAddresses = parseAddresses(to);
  const ccAddresses = parseAddresses(cc);
  const invalid = [...toAddresses, ...ccAddresses].filter(
    (address) => !looksLikeEmail(address),
  );
  const canSend =
    photos.length > 0 &&
    toAddresses.length > 0 &&
    invalid.length === 0 &&
    !sending &&
    photos.length <= 25 &&
    (format === "attachments" || report?.status === "ready");

  const handleSend = async () => {
    if (!canSend || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setError(null);
    try {
      await apiClient.emailPhotos({
        photo_ids: photos.map((photo) => photo.id),
        opportunity: opportunityId,
        job: jobId,
        to: toAddresses,
        cc: ccAddresses,
        subject: subject.trim(),
        message: message.trim(),
        ...(format === "pdf" && report ? { report_id: report.id } : {}),
      });
      onSent?.();
    } catch (err: unknown) {
      const responseData = (err as { responseData?: { error?: string } })
        ?.responseData;
      setError(
        responseData?.error || "Could not send the email. Please try again.",
      );
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !sendingRef.current && onClose()}>
      <ModalDialog
        sx={{
          width: 660,
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <ModalClose disabled={sending} />
        <Typography level="title-lg" startDecorator={<PaperPlaneTilt />}>
          Share / Export {photos.length} photo{photos.length === 1 ? "" : "s"}
        </Typography>
        <Typography level="body-sm" sx={{ color: "text.secondary", mb: 1 }}>
          Keep photos as individual email attachments, or create a branded PDF
          to preview, download, or email.
        </Typography>

        <FormControl>
          <FormLabel>Format</FormLabel>
          <Select
            value={format}
            disabled={sending}
            onChange={(_, value) => {
              if (value) {
                setFormat(value);
                setReport(null);
              }
            }}
          >
            <Option value="attachments">Individual photo attachments</Option>
            <Option value="pdf">Branded PDF</Option>
          </Select>
        </FormControl>
        {photos.length > 25 && (
          <Alert color="warning">Choose up to 25 photos at a time.</Alert>
        )}
        {open && format === "pdf" && (
          <PhotoReportControls
            photos={photos}
            jobId={jobId}
            opportunityId={opportunityId}
            disabled={sending}
            onReady={onReportReady}
          />
        )}
        <Typography level="title-md" sx={{ mt: 1 }}>
          Email (optional for PDFs)
        </Typography>
        {error && (
          <Alert color="danger" variant="soft" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {recipients?.blocked?.length ? (
          <Alert
            color="warning"
            variant="soft"
            startDecorator={<WarningCircle />}
            sx={{ mb: 1 }}
          >
            <Box>
              {recipients.blocked.map((entry) => (
                <Typography
                  key={`${entry.email}-${entry.name}`}
                  level="body-sm"
                >
                  {entry.name || entry.email || "A contact"} was left out —{" "}
                  {entry.reason}
                </Typography>
              ))}
            </Box>
          </Alert>
        ) : null}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <FormControl error={toAddresses.some((a) => !looksLikeEmail(a))}>
            <FormLabel>To</FormLabel>
            <Input
              value={to}
              onChange={(e) => {
                toEdited.current = true;
                setTo(e.target.value);
              }}
              placeholder={
                loadingRecipients
                  ? "Looking up the contact…"
                  : "customer@example.com"
              }
              endDecorator={
                loadingRecipients ? <CircularProgress size="sm" /> : null
              }
            />
            <FormHelperText>
              Separate multiple addresses with commas.
            </FormHelperText>
          </FormControl>

          <FormControl error={ccAddresses.some((a) => !looksLikeEmail(a))}>
            <FormLabel>CC</FormLabel>
            <Input
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="adjuster@example.com"
            />
            {recipients?.cc_suggestions?.length ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  mt: 0.5,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                  Suggested:
                </Typography>
                {recipients.cc_suggestions.map((entry) => (
                  <Chip
                    key={entry.email}
                    size="sm"
                    variant="soft"
                    onClick={() =>
                      setCc((current) =>
                        parseAddresses(current).includes(entry.email)
                          ? current
                          : [...parseAddresses(current), entry.email].join(
                              ", ",
                            ),
                      )
                    }
                  >
                    {entry.name || entry.email}
                  </Chip>
                ))}
              </Box>
            ) : null}
          </FormControl>

          <FormControl>
            <FormLabel>Subject</FormLabel>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Photos from your project"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Message</FormLabel>
            <Textarea
              minRows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a note for the recipient (optional)"
            />
          </FormControl>

          {invalid.length > 0 && (
            <Typography level="body-sm" sx={{ color: "danger.plainColor" }}>
              Not a valid email address: {invalid.join(", ")}
            </Typography>
          )}
        </Box>

        <Box
          sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2 }}
        >
          <Button
            variant="plain"
            color="neutral"
            onClick={onClose}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={!canSend}
            loading={sending}
            startDecorator={sending ? null : <PaperPlaneTilt size={16} />}
          >
            {format === "pdf" ? "Email PDF" : "Email photos"}
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
};
